import { useState } from "react";
import { format } from "date-fns";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { CheckCircle, Clock, XCircle, MessageSquare, AlertCircle } from "lucide-react";

interface ReactivationRequest {
  id: string;
  company_id: string;
  company_name: string;
  membership_id: string;
  status: 'pending' | 'approved' | 'rejected';
  request_message: string | null;
  admin_response_message: string | null;
  created_at: string;
  resolved_at: string | null;
}

interface DeactivatedMembershipWithRequests {
  id: string;
  company_id: string;
  company_name: string;
  role: string;
  deactivated_at: string;
  deactivation_reason: string;
  reactivation_request?: ReactivationRequest;
}

interface ReactivationRequestCardProps {
  membership: DeactivatedMembershipWithRequests;
  onSubmitRequest: (membershipId: string, companyId: string, message?: string) => Promise<void>;
  isLoading: boolean;
}

const ReactivationRequestCard = ({ membership, onSubmitRequest, isLoading }: ReactivationRequestCardProps) => {
  const [message, setMessage] = useState('');
  const [showRequestForm, setShowRequestForm] = useState(false);

  const handleSubmitRequest = async () => {
    await onSubmitRequest(membership.id, membership.company_id, message);
    setMessage('');
    setShowRequestForm(false);
  };

  const handleRequestReactivation = () => {
    setShowRequestForm(true);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-4 w-4" />;
      case 'approved':
        return <CheckCircle className="h-4 w-4" />;
      case 'rejected':
        return <XCircle className="h-4 w-4" />;
      default:
        return <AlertCircle className="h-4 w-4" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'approved':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'rejected':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const request = membership.reactivation_request;
  
  // Simple logic: can request if no valid request exists for current deactivation
  const getTimelineInfo = () => {
    const deactivatedFormatted = format(new Date(membership.deactivated_at), 'dd MMMM yyyy HH:mm');
    
    if (!request) {
      return {
        canRequest: true,
        message: `Deactivated on ${deactivatedFormatted}. You can request reactivation.`
      };
    }
    
    const requestFormatted = format(new Date(request.created_at), 'dd MMMM yyyy HH:mm');
    
    if (request.status === 'pending') {
      return {
        canRequest: false,
        message: `Request submitted on ${requestFormatted}. Waiting for admin review.`
      };
    }
    
    if (request.status === 'rejected') {
      return {
        canRequest: true,
        message: `Request from ${requestFormatted} was rejected. You can submit a new request.`
      };
    }
    
    return {
      canRequest: false,
      message: `Request from ${requestFormatted} was ${request.status}.`
    };
  };
  
  const timelineInfo = getTimelineInfo();

  return (
    <Card className="w-full">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg">{membership.company_name}</CardTitle>
            <CardDescription>
              Previous role: {membership.role} • Deactivated {format(new Date(membership.deactivated_at), 'dd MMMM yyyy')}
            </CardDescription>
          </div>
          {request && (
            <Badge className={`${getStatusColor(request.status)} flex items-center gap-1`}>
              {getStatusIcon(request.status)}
              {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
            </Badge>
          )}
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Timeline and Status Information */}
        <div className="bg-muted/30 p-3 rounded-lg text-sm">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-4 w-4 mt-0.5 text-muted-foreground flex-shrink-0" />
            <div>
              <p className="text-muted-foreground">{timelineInfo.message}</p>
              {membership.deactivation_reason && (
                <p className="text-muted-foreground mt-1">
                  <strong>Reason:</strong> {membership.deactivation_reason}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Existing Request Status */}
        {request && request.status === 'pending' ? (
          <div className="space-y-3">
            <div className="border-l-4 border-yellow-500 bg-yellow-50 pl-4 pr-3 py-2">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="h-4 w-4 text-yellow-600" />
                <span className="text-sm font-medium text-yellow-800">Pending Admin Review</span>
              </div>
              
              <div className="text-sm text-yellow-700 space-y-1">
                <p>Submitted: {format(new Date(request.created_at), 'dd MMMM yyyy HH:mm')}</p>
                <p>An administrator will review your request and respond soon.</p>
              </div>

              {request.request_message && (
                <div className="mt-2">
                  <Label className="text-xs text-yellow-600">Your message:</Label>
                  <p className="text-sm bg-white/50 p-2 rounded mt-1 text-yellow-800">{request.request_message}</p>
                </div>
              )}
            </div>
          </div>
        ) : request && request.status !== 'pending' ? (
          <div className="space-y-3">
            <div className={`border-l-4 pl-4 pr-3 py-2 ${
              request.status === 'approved' 
                ? 'border-green-500 bg-green-50' 
                : 'border-red-500 bg-red-50'
            }`}>
              <div className="flex items-center gap-2 mb-2">
                {request.status === 'approved' ? 
                  <CheckCircle className="h-4 w-4 text-green-600" /> : 
                  <XCircle className="h-4 w-4 text-red-600" />
                }
                <span className={`text-sm font-medium ${
                  request.status === 'approved' ? 'text-green-800' : 'text-red-800'
                }`}>
                  Request {request.status === 'approved' ? 'Approved' : 'Rejected'}
                </span>
              </div>
              
              <div className={`text-sm space-y-1 ${
                request.status === 'approved' ? 'text-green-700' : 'text-red-700'
              }`}>
                <p>Submitted: {format(new Date(request.created_at), 'dd MMMM yyyy HH:mm')}</p>
                {request.resolved_at && (
                  <p>Resolved: {format(new Date(request.resolved_at), 'dd MMMM yyyy HH:mm')}</p>
                )}
              </div>

              {request.request_message && (
                <div className="mt-2">
                  <Label className={`text-xs ${
                    request.status === 'approved' ? 'text-green-600' : 'text-red-600'
                  }`}>Your message:</Label>
                  <p className="text-sm bg-white/50 p-2 rounded mt-1">{request.request_message}</p>
                </div>
              )}

              {request.admin_response_message && (
                <div className="mt-2">
                  <Label className={`text-xs ${
                    request.status === 'approved' ? 'text-green-600' : 'text-red-600'
                  }`}>Admin response:</Label>
                  <p className="text-sm bg-white/50 p-2 rounded mt-1">{request.admin_response_message}</p>
                </div>
              )}
            </div>

            {request.status === 'rejected' && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleRequestReactivation}
                disabled={isLoading}
              >
                Submit New Request
              </Button>
            )}
          </div>
        ) : (
          /* No Request - Show Request Form */
          <div className="space-y-3">
            {showRequestForm ? (
              <div className="space-y-3">
                <div>
                  <Label htmlFor="message">Message to Company Admin (Optional)</Label>
                  <Textarea
                    id="message"
                    placeholder="Explain why you'd like to be reactivated..."
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    className="mt-2"
                    rows={3}
                  />
                </div>
                <div className="flex gap-2">
                  <Button 
                    onClick={handleSubmitRequest}
                    disabled={isLoading}
                    size="sm"
                  >
                    Submit Request
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setShowRequestForm(false);
                      setMessage('');
                    }}
                    size="sm"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <Button 
                onClick={handleRequestReactivation}
                disabled={isLoading || !timelineInfo.canRequest}
                size="sm"
              >
                {timelineInfo.canRequest ? 'Request Reactivation' : 'Cannot Request (Pending)'}
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ReactivationRequestCard;
